import { ROLES } from './roles.js';
import { MODULES } from './modules.js';
export const PERMISSIONS = {
    // Staff (renamed from USERS_*)
    STAFF_VIEW_ALL: 'staff.view.all',
    STAFF_CREATE: 'staff.create',
    STAFF_EDIT: 'staff.edit',
    STAFF_DELETE: 'staff.delete',
    STAFF_EXPORT: 'staff.export',
    // Roles & permission management
    ROLES_ASSIGN: 'roles.assign',
    ROLES_MANAGE: 'roles.manage',
    // Per-user module visibility (granted on Staff Detail Access page)
    MODULES_ASSIGN: 'modules.assign',
    // Profile (self-service)
    PROFILE_VIEW_OWN: 'profile.view.own',
    PROFILE_EDIT_OWN: 'profile.edit.own',
    // FDFC Tracker entries received by the webhook (read-only)
    TRACKER_FDFC_VIEW: 'tracker_fdfc.view',
    TRACKER_FDFC_IMPORT: 'tracker_fdfc.import',
    TRACKER_FDFC_DISPATCH: 'tracker_fdfc.dispatch',
    TRACKER_FDFC_UPDATE: 'tracker_fdfc.update',
    // BDFC Tracker — the same process as FDFC, over the course's own entries
    TRACKER_BDFC_VIEW: 'tracker_bdfc.view',
    TRACKER_BDFC_IMPORT: 'tracker_bdfc.import',
    TRACKER_BDFC_DISPATCH: 'tracker_bdfc.dispatch',
    TRACKER_BDFC_UPDATE: 'tracker_bdfc.update',
};
export const ALL_PERMISSION_CODES = Object.values(PERMISSIONS);
export const PERMISSION_MODULE = {
    [PERMISSIONS.STAFF_VIEW_ALL]: 'staff',
    [PERMISSIONS.STAFF_CREATE]: 'staff',
    [PERMISSIONS.STAFF_EDIT]: 'staff',
    [PERMISSIONS.STAFF_DELETE]: 'staff',
    // 'hrms_staff' (the real module code), not the legacy 'staff' string above —
    // this must stay identical to the `module` value seeded in the migration.
    [PERMISSIONS.STAFF_EXPORT]: 'hrms_staff',
    [PERMISSIONS.ROLES_ASSIGN]: 'roles',
    [PERMISSIONS.ROLES_MANAGE]: 'roles',
    [PERMISSIONS.MODULES_ASSIGN]: 'staff',
    [PERMISSIONS.PROFILE_VIEW_OWN]: 'profile',
    [PERMISSIONS.PROFILE_EDIT_OWN]: 'profile',
    // Must stay identical to the `module` value seeded in the migration.
    [PERMISSIONS.TRACKER_FDFC_VIEW]: 'tracker_fdfc',
    [PERMISSIONS.TRACKER_FDFC_IMPORT]: 'tracker_fdfc',
    [PERMISSIONS.TRACKER_FDFC_DISPATCH]: 'tracker_fdfc',
    [PERMISSIONS.TRACKER_FDFC_UPDATE]: 'tracker_fdfc',
    [PERMISSIONS.TRACKER_BDFC_VIEW]: 'tracker_bdfc',
    [PERMISSIONS.TRACKER_BDFC_IMPORT]: 'tracker_bdfc',
    [PERMISSIONS.TRACKER_BDFC_DISPATCH]: 'tracker_bdfc',
    [PERMISSIONS.TRACKER_BDFC_UPDATE]: 'tracker_bdfc',
};
/**
 * Maps each permission to the module it lives inside. Used by the backend
 * `requirePermission` plugin to enforce the dual check: user must have the
 * permission AND own the module that permission belongs to.
 */
export const PERMISSION_MODULE_CODE = {
    [PERMISSIONS.STAFF_VIEW_ALL]: MODULES.HRMS_STAFF,
    [PERMISSIONS.STAFF_CREATE]: MODULES.HRMS_STAFF,
    [PERMISSIONS.STAFF_EDIT]: MODULES.HRMS_STAFF,
    [PERMISSIONS.STAFF_DELETE]: MODULES.HRMS_STAFF,
    [PERMISSIONS.STAFF_EXPORT]: MODULES.HRMS_STAFF,
    [PERMISSIONS.ROLES_ASSIGN]: MODULES.HRMS_STAFF,
    [PERMISSIONS.ROLES_MANAGE]: MODULES.MASTER_ROLES,
    [PERMISSIONS.MODULES_ASSIGN]: MODULES.HRMS_STAFF,
    [PERMISSIONS.PROFILE_VIEW_OWN]: MODULES.PROFILE,
    [PERMISSIONS.PROFILE_EDIT_OWN]: MODULES.PROFILE,
    [PERMISSIONS.TRACKER_FDFC_VIEW]: MODULES.TRACKER_FDFC,
    [PERMISSIONS.TRACKER_FDFC_IMPORT]: MODULES.TRACKER_FDFC,
    [PERMISSIONS.TRACKER_FDFC_DISPATCH]: MODULES.TRACKER_FDFC,
    [PERMISSIONS.TRACKER_FDFC_UPDATE]: MODULES.TRACKER_FDFC,
    [PERMISSIONS.TRACKER_BDFC_VIEW]: MODULES.TRACKER_BDFC,
    [PERMISSIONS.TRACKER_BDFC_IMPORT]: MODULES.TRACKER_BDFC,
    [PERMISSIONS.TRACKER_BDFC_DISPATCH]: MODULES.TRACKER_BDFC,
    [PERMISSIONS.TRACKER_BDFC_UPDATE]: MODULES.TRACKER_BDFC,
};
export const PERMISSION_DESCRIPTION = {
    [PERMISSIONS.STAFF_VIEW_ALL]: 'View all staff in the organization',
    [PERMISSIONS.STAFF_CREATE]: 'Create new staff accounts',
    [PERMISSIONS.STAFF_EDIT]: 'Edit existing staff records',
    [PERMISSIONS.STAFF_DELETE]: 'Soft-delete (deactivate) staff accounts',
    [PERMISSIONS.STAFF_EXPORT]: 'Export the staff register to Excel',
    [PERMISSIONS.ROLES_ASSIGN]: 'Assign roles to staff',
    [PERMISSIONS.ROLES_MANAGE]: 'Manage role-permission and role default-module mappings',
    [PERMISSIONS.MODULES_ASSIGN]: 'Grant or revoke module visibility for a specific staff user',
    [PERMISSIONS.PROFILE_VIEW_OWN]: 'View own profile',
    [PERMISSIONS.PROFILE_EDIT_OWN]: 'Edit own profile',
    [PERMISSIONS.TRACKER_FDFC_VIEW]: 'View FDFC Tracker entries received by the webhook',
    [PERMISSIONS.TRACKER_FDFC_IMPORT]: 'Import FDFC Tracker entries from Excel',
    [PERMISSIONS.TRACKER_FDFC_DISPATCH]: 'Send FDFC Tracker entries to NSDC as kit orders',
    [PERMISSIONS.TRACKER_FDFC_UPDATE]: 'Record calls and survey answers on FDFC Tracker entries',
    [PERMISSIONS.TRACKER_BDFC_VIEW]: 'View BDFC Tracker entries received by the webhook',
    [PERMISSIONS.TRACKER_BDFC_IMPORT]: 'Import BDFC Tracker entries from Excel',
    [PERMISSIONS.TRACKER_BDFC_DISPATCH]: 'Send BDFC Tracker entries to NSDC as kit orders',
    [PERMISSIONS.TRACKER_BDFC_UPDATE]: 'Record calls and survey answers on BDFC Tracker entries',
};
export const ROLE_PERMISSION_SEED = {
    [ROLES.SUPER_ADMIN]: [...ALL_PERMISSION_CODES],
    [ROLES.ADMIN]: [
        PERMISSIONS.STAFF_VIEW_ALL,
        PERMISSIONS.STAFF_CREATE,
        PERMISSIONS.STAFF_EDIT,
        PERMISSIONS.STAFF_DELETE,
        PERMISSIONS.STAFF_EXPORT,
        PERMISSIONS.ROLES_ASSIGN,
        PERMISSIONS.MODULES_ASSIGN,
        PERMISSIONS.PROFILE_VIEW_OWN,
        PERMISSIONS.PROFILE_EDIT_OWN,
        PERMISSIONS.TRACKER_FDFC_VIEW,
        PERMISSIONS.TRACKER_FDFC_IMPORT,
        PERMISSIONS.TRACKER_FDFC_DISPATCH,
        PERMISSIONS.TRACKER_FDFC_UPDATE,
        PERMISSIONS.TRACKER_BDFC_VIEW,
        PERMISSIONS.TRACKER_BDFC_IMPORT,
        PERMISSIONS.TRACKER_BDFC_DISPATCH,
        PERMISSIONS.TRACKER_BDFC_UPDATE,
    ],
    [ROLES.MANAGER]: [
        PERMISSIONS.STAFF_VIEW_ALL,
        PERMISSIONS.STAFF_EXPORT,
        PERMISSIONS.PROFILE_VIEW_OWN,
        PERMISSIONS.PROFILE_EDIT_OWN,
    ],
    [ROLES.TEAM_LEADER]: [
        PERMISSIONS.PROFILE_VIEW_OWN,
        PERMISSIONS.PROFILE_EDIT_OWN,
    ],
    [ROLES.USER]: [
        PERMISSIONS.PROFILE_VIEW_OWN,
        PERMISSIONS.PROFILE_EDIT_OWN,
    ],
};
//# sourceMappingURL=permissions.js.map